import {ExtensionSettingEnum, ModeSettingEnum, ScenarioSettingEnum, TemporaryModeSettingEnum} from '@colonist/game-base-types'
import {ArrayUtils} from '@colonist/utils'
import * as GameMode from './GameModeData'
import {TranslationObject} from '@colonist/i18n'
import {BaseScenarioData} from './BaseScenarioData'
import {BaseGameModeData} from './BaseGameModeData'
import {ScenarioDataFogIsland, ScenarioDataFourIslands, ScenarioDataHeadingForNewShores, ScenarioDataNone, ScenarioDataThroughTheDesert} from './ScenarioData'

type InstantiableBaseScenarioData = new () => BaseScenarioData

export class GameModeDataController {
    static publicGameModeDataList: BaseGameModeData[] = [
        new GameMode.ModeClassic4PData(), new GameMode.ModeClassic56PData(), new GameMode.ModeClassic78PData(),

        new GameMode.ModeSeafarers4PData(), new GameMode.ModeSeafarers56PData(), new GameMode.ModeCitiesAndKnights4PData(), new GameMode.ModeCitiesAndKnights56PData(),

        new GameMode.ModeSeafarersAndCitiesAndKnights4PData(), new GameMode.ModeSeafarersAndCitiesAndKnights56PData(),

        new GameMode.ModeServerStressTestClassic4P(),
    ]

    static privateGameModeDataList: BaseGameModeData[] = [
        ...GameModeDataController.publicGameModeDataList, new GameMode.ModeClassicBeginner4PData(), new GameMode.ModeClassicTutorial4PData(),
    ]

    private static readonly scenarioDataClasses: {[scenario in ScenarioSettingEnum]: InstantiableBaseScenarioData} = {
        [ScenarioSettingEnum.None]: ScenarioDataNone,
        [ScenarioSettingEnum.SS1HeadingForNewShores]: ScenarioDataHeadingForNewShores,
        [ScenarioSettingEnum.SS2TheFourIslands]: ScenarioDataFourIslands,
        [ScenarioSettingEnum.SS3TheFogIslands]: ScenarioDataFogIsland,
        [ScenarioSettingEnum.SS4ThroughTheDesert]: ScenarioDataThroughTheDesert,
    }

    static getModeDataForMode(modeSetting: ModeSettingEnum): BaseGameModeData | undefined {
        for(const gameModeData of GameModeDataController.privateGameModeDataList) {
            if(gameModeData.modeSetting == modeSetting) return gameModeData.getNewInstanceOfData()
        }
        return undefined
    }

    static getModeName(modeSetting: ModeSettingEnum): TranslationObject {
        const gameModeText = this.getModeDataForMode(modeSetting)?.name
        const defaultGameModeName: TranslationObject = {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {value: ModeSettingEnum[modeSetting]},
        }
        return gameModeText ?? defaultGameModeName
    }

    static getScenarioDataForScenario(scenarioSetting: ScenarioSettingEnum): BaseScenarioData | undefined {
        const scenarioDataClass = this.scenarioDataClasses[scenarioSetting]
        return new scenarioDataClass()
    }

    static getMaxAllowablePlayerCountForMode(modeSetting: ModeSettingEnum): number {
        const allowablePlayerCount: number[] = []

        const availableGameModeData = GameModeDataController.getAvailableGameModeDataForTemporaryMode(modeSetting)

        for(const gameModeData of availableGameModeData) {
            const allowablePlayerCountForExtension = gameModeData.getAllowablePlayerCount()
            allowablePlayerCount.push(...allowablePlayerCountForExtension)
        }
        return Math.max(...allowablePlayerCount)
    }

    static getAvailableGameModeDataForTemporaryMode(modeSetting: ModeSettingEnum): BaseGameModeData[] {
        const availableGameModeDataForTemporaryMode: BaseGameModeData[] = []
        const mainGameModeData = GameModeDataController.getModeDataForMode(modeSetting)
        if(mainGameModeData == undefined) return availableGameModeDataForTemporaryMode

        const temporaryModeSetting = mainGameModeData.temporaryModeSetting
        for(const gameModeData of GameModeDataController.privateGameModeDataList) {
            if(gameModeData.temporaryModeSetting != temporaryModeSetting) continue
            availableGameModeDataForTemporaryMode.push(gameModeData)
        }
        return availableGameModeDataForTemporaryMode
    }

    static getAvailableExtensionsForTemporaryMode(temporaryModeSetting: TemporaryModeSettingEnum): ExtensionSettingEnum[] {
        const availableExtensions: ExtensionSettingEnum[] = []
        for(const gameModeData of GameModeDataController.privateGameModeDataList) {
            if(gameModeData.temporaryModeSetting != temporaryModeSetting) continue
            if(ArrayUtils.elementInArray(availableExtensions, gameModeData.extensionSetting)) continue
            availableExtensions.push(gameModeData.extensionSetting)
        }
        return availableExtensions
    }

    static getGameModeDataForPlayerCountAndMode(playerCount: number, modeSetting: ModeSettingEnum): BaseGameModeData | undefined {
        const availableExtensionsForTemporaryMode = GameModeDataController.getAvailableGameModeDataForTemporaryMode(modeSetting)
        for(const gameModeDate of availableExtensionsForTemporaryMode) {
            const allowablePlayercount = gameModeDate.getAllowablePlayerCount()
            if(ArrayUtils.elementInArray(allowablePlayercount, playerCount)) return gameModeDate
        }
        return undefined
    }

    static isModeCitiesAndKnights(modeSetting: ModeSettingEnum): boolean {
        if(modeSetting == ModeSettingEnum.CitiesAndKnights4P) return true
        if(modeSetting == ModeSettingEnum.CitiesAndKnights56P) return true
        if(modeSetting == ModeSettingEnum.CitiesAndKnightsSeafarers4P) return true
        if(modeSetting == ModeSettingEnum.CitiesAndKnightsSeafarers56P) return true

        return false
    }
}
